import React, { useEffect, useMemo, useState } from 'react';
import theme from './StyleDetails.module.scss';
import { useParams } from 'react-router-dom';
import { Breadcrumbs, classnames, LoadingIndicator, MarkdownDisplay } from '@ygtechnologies/bygr-frontend-component-library';
import { AlertBanner, Button, Card, Carousel, SimpleTooltip } from '@ygtechnologies/rubus-ui';
import { BuyerCurrencyDisplay } from '@/buyer/components/BuyerCurrencyDisplay';
import { Trans } from '@lingui/react/macro';
import { useOptionConfigurator } from '../OptionConfiguratorContext';
import { useConfiguratorMutations } from '../ConfiguratorMutationContext';
import { useStylePreviewQuery } from '@/services/configurator/';
import { useBuyerRoomMap } from '@/services/rooms';
import { useImageUrls } from '../useImageUrls';
import { ProductCardList } from '@/seller/components/products/ProductCard';
import { BuyerOptionCard } from '../BuyerOptionCard';
import { BuyerOptionAccordion } from '../BuyerOptionAccordion';
import { SelectedIndicator } from '../SelectStyleBtn/SelectedIndicator';
import { buildConfiguratorUrl } from '../utils/buildConfiguratorUrl';
import { faTag } from '@fortawesome/pro-light-svg-icons';
import { getRoomDisplayName, groupConfiguratorRooms } from '../utils/configuratorRooms';

export interface StyleDetailsProps {}

export const StyleDetails: React.FC<StyleDetailsProps> = () => {
    const {
        styleId: styleIdParam,
        styleGroupId,
        roomId,
    } = useParams<{ styleId: string; styleGroupId: string; roomId?: string }>();
    const {
        baseUrl,
        allDeadlinesConfiguratorData,
        residentialUnitId,
        selectedDeadlineId,
        getRoomMeta,
        setTabsEnabled,
        viewOnly,
        isShowroom,
    } = useOptionConfigurator();
    const { pickStyle, isPickingStyle } = useConfiguratorMutations();
    const { getOptionPresetImageUrl } = useImageUrls();

    useEffect(() => {
        setTabsEnabled(false);
        return () => {
            setTabsEnabled(true);
        };
    }, []);

    const [activeRoomTabId, setActiveRoomTabId] = useState('all');

    // Room-scoped style groups appear once per room with the same id, so the lookup must
    // match the room from the URL too — previewRoomId and the Select-button roomId below
    // would otherwise resolve to whichever room happens to come first in the payload.
    const styleGroup = allDeadlinesConfiguratorData?.styleGroups.find(
        (x) => x.id === Number(styleGroupId) && x.roomId === (roomId ? Number(roomId) : x.roomId)
    );

    const styleId = Number(styleIdParam);
    const style = styleGroup?.styles.find((s) => s.id === styleId);

    // Backend simulation of the configurator as-if this style were picked. The option list
    // below renders from this, so prices/selections are what the buyer would actually get
    // (e.g. free defaults under "Default style option free") instead of current-state data.
    // The header (name/price/isPicked) keeps using the current-state `style` above — the
    // preview reports the previewed style itself as picked.
    const previewRoomId = styleGroup?.roomId ?? null;
    const stylePreviewQuery = useStylePreviewQuery(residentialUnitId, style?.id, previewRoomId);
    const roomsMap = useBuyerRoomMap(residentialUnitId);

    const styleImages = useMemo(
        () =>
            (style?.images ?? []).map((image) => ({
                src: getOptionPresetImageUrl(image.id),
                thumbnailSrc: getOptionPresetImageUrl(image.id),
                type: 'image' as const,
            })),
        [style?.images, getOptionPresetImageUrl]
    );

    const fixedOptionsForStyle = useMemo(() => {
        const previewGroups = stylePreviewQuery.data?.groups;
        if (!previewGroups) {
            return [];
        }

        // The preview already carries the as-if-applied selected/isDefault flags and prices;
        // we only narrow each group down to the options that belong to this style.
        return groupConfiguratorRooms(previewGroups, roomsMap).map((room) => ({
            ...room,
            groups: room.groups.map((group) => ({
                ...group,
                options: group.options.filter((option) => option.presets.some((p) => p.presetId === styleId)),
            })),
        }));
    }, [stylePreviewQuery.data, roomsMap, styleId]);

    const roomsWithOptions = useMemo(
        () => fixedOptionsForStyle.filter((room) => room.groups.some((group) => group.options.length > 0)),
        [fixedOptionsForStyle]
    );

    const allOptions = useMemo(
        () =>
            roomsWithOptions.flatMap((room) =>
                room.groups.flatMap((group) =>
                    group.options.map((option) => ({
                        option,
                        groupName: group.name,
                    }))
                )
            ),
        [roomsWithOptions]
    );

    useEffect(() => {
        const roomTabIds = roomsWithOptions.map((room) => room.roomId.toString());
        const validTabIds = new Set<string>(['all', ...roomTabIds]);

        if (validTabIds.has(activeRoomTabId)) {
            return;
        }

        if (allOptions.length > 0) {
            setActiveRoomTabId('all');
            return;
        }

        if (roomTabIds[0]) {
            setActiveRoomTabId(roomTabIds[0]);
        }
    }, [activeRoomTabId, allOptions.length, roomsWithOptions]);

    if (!allDeadlinesConfiguratorData || stylePreviewQuery.isLoading) {
        return <LoadingIndicator />;
    }

    // Early returns must come after every hook above (rules of hooks).
    if (!style) {
        return 'Not found?';
    }
    return (
        <Card className={theme.detailsCard}>
            <Breadcrumbs>
                {styleGroup?.roomId ? (
                    <Breadcrumbs.Link
                        to={buildConfiguratorUrl({
                            baseUrl,
                            deadlineId: selectedDeadlineId,
                            segment: `room/${styleGroup.roomId}`,
                        })}>
                        {getRoomMeta(styleGroup.roomId)?.name}
                    </Breadcrumbs.Link>
                ) : (
                    <Breadcrumbs.Link
                        to={buildConfiguratorUrl({
                            baseUrl,
                            deadlineId: selectedDeadlineId,
                            segment: `style-group/${styleGroupId}`,
                        })}>
                        <Trans>Main style</Trans>
                    </Breadcrumbs.Link>
                )}
                <Breadcrumbs.Item>{style.name}</Breadcrumbs.Item>
            </Breadcrumbs>
            {styleImages.length > 0 && (
                <Carousel className={theme.headerCarousel} src={styleImages} height={350} mode="indicators" enableLightbox />
            )}

            <div className={theme.detailsContent}>
                <div className={theme.header}>
                    <span className={theme.headerTitle}>{style.name}</span>
                    <span className={theme.headerPrice}>
                        <BuyerCurrencyDisplay value={style.price} />
                    </span>
                    {style.isPicked ? (
                        <SelectedIndicator />
                    ) : styleGroup?.isLocked && !isShowroom ? (
                        <SimpleTooltip content={<Trans>This style group is locked</Trans>}>
                            <Button disabled>
                                <Trans>Select style</Trans>
                            </Button>
                        </SimpleTooltip>
                    ) : viewOnly ? (
                        <SimpleTooltip content={<Trans>Selections are disabled in this view</Trans>}>
                            <Button disabled>
                                <Trans>Select style</Trans>
                            </Button>
                        </SimpleTooltip>
                    ) : (
                        <Button
                            loading={isPickingStyle}
                            // Room-scoped style groups must pick per room, matching the preview scope.
                            onClick={() => pickStyle(style.id, styleGroup?.roomId ?? undefined)}>
                            <Trans>Select style</Trans>
                        </Button>
                    )}
                </div>
                <MarkdownDisplay className={theme.description} markdown={style.description} />
            </div>

            <h3 className={theme.roomsTitle}>
                <Trans>Available selections in this style</Trans>
            </h3>
            <AlertBanner type="info" className={theme.selectionInfo} title={<Trans>Read-only view</Trans>}>
                <Trans>
                    This view shows which options are included in the style. You cannot make selections here. To choose
                    products, first select a style and then pick options for each room.
                </Trans>
            </AlertBanner>
            {stylePreviewQuery.isError ? (
                // Never fall back to current-state data here — those prices are wrong for a
                // non-picked style, which is the bug this preview exists to fix.
                <AlertBanner type="danger" title={<Trans>Could not load the options in this style</Trans>}>
                    <Trans>Something went wrong while loading the style preview.</Trans>{' '}
                    <Button variant="tertiary" size="sm" onClick={() => stylePreviewQuery.refetch()}>
                        <Trans>Try again</Trans>
                    </Button>
                </AlertBanner>
            ) : (
                <>
                    <Card className={theme.tabsCard}>
                        <div className={theme.tabsRow}>
                            <Button
                                variant="dark-link"
                                className={classnames(theme.tabButton, { [theme.tabActive]: activeRoomTabId === 'all' })}
                                onClick={() => setActiveRoomTabId('all')}>
                                <Trans>All options</Trans>
                            </Button>
                            {roomsWithOptions.map((configuratorRoom) => {
                                return (
                                    <Button
                                        key={configuratorRoom.roomId}
                                        variant="dark-link"
                                        className={classnames(theme.tabButton, {
                                            [theme.tabActive]: activeRoomTabId === configuratorRoom.roomId.toString(),
                                        })}
                                        onClick={() => setActiveRoomTabId(configuratorRoom.roomId.toString())}
                                        icon={configuratorRoom.icon ? ['fal', configuratorRoom.icon] : faTag}>
                                        {getRoomDisplayName(configuratorRoom)}
                                    </Button>
                                );
                            })}
                        </div>
                    </Card>

                    {(activeRoomTabId === 'all'
                        ? roomsWithOptions
                        : roomsWithOptions.filter((room) => room.roomId.toString() === activeRoomTabId)
                    ).flatMap((configuratorRoom) =>
                        configuratorRoom.groups.map((group) =>
                            group.options.length === 0 ? null : (
                                <BuyerOptionAccordion key={`${configuratorRoom.roomId}-${group.id}`} group={group}>
                                    <ProductCardList>
                                        {group.options.map((option) => (
                                            <BuyerOptionCard
                                                key={option.id}
                                                option={option}
                                                groupName={group.name}
                                                is3d={false}
                                                hideSelectionSlot
                                            />
                                        ))}
                                    </ProductCardList>
                                </BuyerOptionAccordion>
                            )
                        )
                    )}
                </>
            )}
        </Card>
    );
};
